import React from 'react';
import { Field as FinalField, useForm } from 'react-final-form';
import Field from '@lendi-ui/field';

import { FIELD_VALUES } from '@funnels/constants';
import { useUpdatedRef } from '@funnels/hooks';
import { getAriaLabelById, validateNotUndefined } from '@funnels/utils';

import { FieldLabel } from '../_common/FieldLabel';
import { FieldWrapper } from '../_common/style';
import { CommonFieldProps } from '../_common/types';
import { RadioButtonBlock, RadioButtonInline, StyledRadioGroup } from './index.style';

export interface RadioButtonsRadios {
  isDisabled?: boolean;
  label: string;
  value: string;
}

export interface RadioButtonsFieldProps extends CommonFieldProps {
  direction?: 'row' | 'column';
  isDisabled?: boolean;
  isBoxed?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  radios: RadioButtonsRadios[];
  selectedValue?: string | boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  noVerticalSpace?: boolean;
  setInParentForm?: boolean;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

const RadioButtonsField = (props: RadioButtonsFieldProps) => {
  const {
    direction,
    isDisabled,
    isOptional,
    isBoxed,
    label,
    labelAfter,
    name,
    onChange,
    radios,
    selectedValue,
    subLabel,
    validationFailMessage,
    size = 'md',
    noVerticalSpace,
    'aria-labelledby': ariaLabelledby,
    'aria-describedby': ariaDescribedby,
    disableFailMessage = false,
    setInParentForm = true,
    ...rest
  } = props;
  const form = useForm();
  const [localValue, setLocalValue] = React.useState<string>(() => parseBoolean(selectedValue) || '');
  const StyledRadioButton = direction === 'row' ? RadioButtonInline : RadioButtonBlock;
  const validationRef = useUpdatedRef({ isDisabled, isOptional });
  const validate = (value: any, formValues: any) => {
    if (validationRef.current.isDisabled) {
      return;
    }
    if (props.validate) {
      return props.validate(value, formValues);
    }

    return validateNotUndefined(validationFailMessage, validationRef.current.isOptional)(value);
  };
  const fieldProps = {
    name,
    validate,
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue: string | boolean = e.target.value;

    // Set in local state
    setLocalValue(newValue);

    // Transfer "TRUE" and "FALSE" to boolean true and false
    if (newValue === FIELD_VALUES.TRUE) {
      newValue = true;
    } else if (newValue === FIELD_VALUES.FALSE) {
      newValue = false;
    }

    if (onChange) {
      onChange(e);
    }
    // Set in parent form
    setInParentForm && form.change(name, newValue);
  };

  function parseBoolean(value?: string | boolean) {
    // Transfer boolean true and false to "TRUE" and "FALSE"
    if (value === true || value === 'true') {
      value = FIELD_VALUES.TRUE;
    } else if (value === false || value === 'false') {
      value = FIELD_VALUES.FALSE;
    }
    return value;
  }

  React.useEffect(() => {
    setLocalValue(parseBoolean(selectedValue) || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValue]);

  return (
    <FieldWrapper>
      <FieldLabel label={label} labelAfter={labelAfter} subLabel={subLabel} idPostfix={name} isOptional={isOptional} />
      <FinalField {...fieldProps}>
        {({ input, meta }) => (
          <Field {...meta} error={disableFailMessage ? undefined : meta.error}>
            <StyledRadioGroup
              isDisabled={isDisabled}
              data-component={name}
              direction={direction}
              selectedValue={localValue}
              onChange={handleChange}
              isBoxed={isBoxed ?? true}
              size={size}
              {...rest}
            >
              {radios.map((radio, index) => (
                <StyledRadioButton
                  key={index}
                  label={radio.label}
                  value={radio.value}
                  isDisabled={radio.isDisabled}
                  aria-label={radio.label}
                  noVerticalSpace={noVerticalSpace}
                  aria-labelledby={getAriaLabelById('label', label ? name : undefined, ariaLabelledby)}
                  aria-describedby={getAriaLabelById('description', subLabel ? name : undefined, ariaDescribedby)}
                />
              ))}
            </StyledRadioGroup>
          </Field>
        )}
      </FinalField>
    </FieldWrapper>
  );
};

export default RadioButtonsField;
